import { matchPath } from 'react-router';
import { createSelector } from 'reselect';

import { EXTENSION, ROLE } from 'components/common/AccessControlType';
import { haveRouteAccess } from 'components/common/RightsUtils';
import { hasAccess } from 'components/common/utils/hasAccess';
import { PARTS, SALES, TECHNICIAN } from 'constants/ModeConstants';
import { EXTENSIONS, FEATURES, FLEETBACK_PACK, ODITOO_PACK, ROLES } from 'constants/RightsConstants';
import { PATH } from 'constants/RoutesConstants';
import {
  ADMIN,
  CUSTOMER,
  SALESMAN,
  SCHEDULER,
  READ_ONLY,
  ALL_ROLES,
  INTEGRATION,
  BACK_OFFICE,
  MANUFACTURER,
  SALES_MANAGER,
  GROUP_MANAGER,
  PARTS_MANAGER,
  VO_STOCK_MANAGER,
  LEADERSHIP_ROLES,
  CAMPAIGN_MANAGER,
  AFTER_SALES_MANAGER,
  LEADERS_AND_MANAGERS,
  NO_CONFIGURATION_ACCESS,
  INTERVENTION_ROLES_WO_SELF_SERVICE,
} from 'constants/UserConstants';
import { getAllBrandsIds } from 'modules/brands/selectors';
import { CHANNELS } from 'modules/notifications/constants';
import { isSelfServiceOnly } from 'modules/selfServices/utils';

import { getCurrentLocation } from 'selectors/router';
import { getProps, getSecondProps } from 'utils/selectors';

/** @returns {import("../types/currentUser").CurrentUserState} */
export const getCurrentUser = state => state.newUsers.currentUser;

export const haveInitializeSession = createSelector(getCurrentUser, state => state?.isInit);

export const getCurrentUserIsLoading = createSelector(getCurrentUser, user => user.isLoading);

export const getCurrentUserData = createSelector(getCurrentUser, ({ data }) => data);

export const getCurrentUserJWTToken = createSelector(getCurrentUserData, ({ jwtToken }) => jwtToken);

export const getCurrentUserRoles = createSelector(getCurrentUserData, ({ roles = '' }) => roles.split(','));

export const getCurrentUserBrandIds = createSelector(
  getCurrentUserData,
  ({ additionalData = {} }) => additionalData.brandIds
);

export const getCurrentUserBrandIdsOrAllBrands = createSelector(
  getCurrentUserData,
  getAllBrandsIds,
  ({ additionalData = {} }, brandIds) => additionalData.brandIds ?? brandIds
);

export const getCurrentUserIsGroupManager = createSelector(getCurrentUserRoles, roles => roles.includes(GROUP_MANAGER));

export const getCurrentUserIsManufacturer = createSelector(getCurrentUserRoles, roles => roles.includes(MANUFACTURER));

export const getCurrentUserIsStockVOManager = createSelector(getCurrentUserRoles, roles =>
  roles.includes(VO_STOCK_MANAGER));

/**
 * @return {boolean}
 */
export const getCurrentUserIsAdmin = createSelector(getCurrentUserRoles, roles => roles.includes(ADMIN));

export const isCurrentUserIsAdminOrGroupManager = createSelector(
  getCurrentUserIsAdmin,
  getCurrentUserIsGroupManager,
  (isAdmin, isGroupManager) => isAdmin || isGroupManager
);

export const isCurrentUserAdminOrManufacturer = createSelector(
  getCurrentUserIsAdmin,
  getCurrentUserIsManufacturer,
  (isAdmin, isManufacturer) => isAdmin || isManufacturer
);

const isCurrentUserDealerManager = createSelector(getCurrentUserRoles, roles =>
  hasAccess(roles, [SALES_MANAGER, AFTER_SALES_MANAGER]));

export const isCurrentUserSalesManager = createSelector(getCurrentUserRoles, roles => roles.includes(SALES_MANAGER));

const isCurrentUserAfterSalesManager = createSelector(getCurrentUserRoles, roles =>
  roles.includes(AFTER_SALES_MANAGER));

export const isCurrentUserAdministrativeOfficer = createSelector(getCurrentUserRoles, roles =>
  roles.includes(BACK_OFFICE));

const isCurrentUserPartsManager = createSelector(getCurrentUserRoles, roles => roles.includes(PARTS_MANAGER));

export const isCurrentUserCampaignManager = createSelector(getCurrentUserRoles, roles =>
  roles.includes(CAMPAIGN_MANAGER));

export const getCurrentUserIsReadOnly = createSelector(getCurrentUserRoles, roles => roles.includes(READ_ONLY));

export const getCurrentUserIsSalesman = createSelector(getCurrentUserRoles, roles => roles.includes(SALESMAN));

export const getCurrentUserExtensions = createSelector(
  getCurrentUserData,
  ({ additionalData = {} }) => additionalData.extensions
);

export const getCurrentUserHasExtension = createSelector(
  getCurrentUserExtensions,
  getSecondProps,
  (extensions, extension) => extensions.includes(extension)
);

export const getCurrentUserFleetbackPack = createSelector(
  getCurrentUserData,
  ({ additionalData = {} }) => additionalData.fleetbackPack
);

export const getCurrentUserOditooPack = createSelector(
  getCurrentUserData,
  ({ additionalData = {} }) => additionalData.oditooPack
);

export const getCurrentUserFeatures = createSelector(
  [getCurrentUserData],
  ({ additionalData = {} }) => additionalData.features ?? []
);

export const getCurrentUsername = state => getCurrentUserData(state).username;

export const getCurrentUserEmail = createSelector(
  [getCurrentUserData],
  ({ additionalData = {} }) => additionalData.email ?? ''
);

export const getCurrentUserId = createSelector(getCurrentUserData, state => state.userId);

export const getCurrentUserGroupId = createSelector(
  getCurrentUserData,
  ({ additionalData = {} }) => additionalData.groupId
);

export const getUserNotificationConfig = createSelector(
  getCurrentUserData,
  ({ additionalData = {} }) => additionalData.notificationConfig
);

const getUserNotificationConfigByNotificationSetting = createSelector(
  getUserNotificationConfig,
  getProps,
  (notificationConfig, { setting, extension }) => notificationConfig[extension]?.[setting]
);

export const userNotificationConfigIsEnabled = createSelector(
  getUserNotificationConfigByNotificationSetting,
  ({ channels = [] } = {}) => channels.includes(CHANNELS.WEB)
);

export const getCurrentUserDealerIds = createSelector(
  getCurrentUserData,
  ({ additionalData = {} }) => additionalData.dealerIds
);

export const getCurrentUserCountries = createSelector(
  getCurrentUserData,
  ({ additionalData = {} }) => additionalData.countries // Only manufacturers have this prop
);

const rolesToIgnore = [CUSTOMER, READ_ONLY, NO_CONFIGURATION_ACCESS, SCHEDULER, ADMIN, MANUFACTURER, GROUP_MANAGER];

const ROLES_FILTERABLE = ALL_ROLES.filter(role => !rolesToIgnore.includes(role));

export const getFilterableRoles = createSelector(
  getCurrentUserIsAdmin,
  isCurrentUserDealerManager,
  (isAdmin, isDealerManager) => [
    ...ROLES_FILTERABLE,
    ...(isAdmin ? [ADMIN, MANUFACTURER, INTEGRATION] : []),
    ...(isDealerManager ? [] : [GROUP_MANAGER]),
  ]
);

export const getCurrentUserAdditionalData = createSelector(getCurrentUser, state => state.data?.additionalData);

export const getUserCustomBranding = createSelector(
  getCurrentUserData,
  ({ additionalData = {} }) => additionalData.customBranding ?? {}
);

export const getCustomName = createSelector(getUserCustomBranding, ({ name }) => name ?? 'Fleetback');

export const isCustomBranding = createSelector(
  getUserCustomBranding,
  ({ logoUrl, webLabelUrl }) => logoUrl && webLabelUrl
);

export const isProfilePage = createSelector(getCurrentLocation, getCurrentUserId, (location, currentUserId) => {
  const detailedMatch = matchPath(PATH.USERS_DETAILED.path, location);
  const isMyDetailsPage = detailedMatch !== null && detailedMatch.params.userId === currentUserId;
  const isProfilePath = matchPath(PATH.PROFILE.path, location);

  return isProfilePath || isMyDetailsPage;
});

export const hasManagerRole = createSelector(getCurrentUserRoles, roles =>
  hasAccess(roles, LEADERS_AND_MANAGERS));

export const hasSalesManagingRole = createSelector(getCurrentUserRoles, roles =>
  hasAccess(roles, [...LEADERSHIP_ROLES, SALES_MANAGER]));

export const getUserAccessMatrix = createSelector(
  getCurrentUserRoles,
  getCurrentUserFeatures,
  getCurrentUserOditooPack,
  getCurrentUserExtensions,
  getCurrentUserFleetbackPack,
  (roles, features, oditooPacks, extensions, fleetbackPacks) => ({
    [ROLES]: roles,
    [FEATURES]: features,
    [EXTENSIONS]: extensions,
    [ODITOO_PACK]: [oditooPacks],
    [FLEETBACK_PACK]: [fleetbackPacks],
  })
);

export const canAccessTo = createSelector(getProps, getUserAccessMatrix, (path, matrix) => haveRouteAccess(path, matrix));

export const hasPushNotificationsEnabled = createSelector(getCurrentUserAdditionalData, ({ token }) => Boolean(token));

/**
 * @param {{
 *   isAdmin: boolean,
 *   roles: import('modules/users/types/users.enum').UserRoleEnum[],
 *   extensions: import('modules/global/typing/enums/extensions.enum').ExtensionEnum[] | import('modules/users/types/users.enum').UserRoleEnum[]
 * } & import('components/common/AccessControl').UseAccessControlProps} params
 * @returns {boolean}
 */
const checkAccessControl = ({ roles, isAdmin, extensions, authsType = ROLE, allowedAuths = [] }) => {
  const isExtensionAuth = authsType === EXTENSION;
  const auths = isExtensionAuth ? extensions : roles;
  const allAllowedAuths = allowedAuths.length === 0;
  return (isAdmin && isExtensionAuth) || allAllowedAuths || hasAccess(allowedAuths, auths);
};

export const matchAccessControl = createSelector(
  getCurrentUserIsAdmin,
  getCurrentUserExtensions,
  getCurrentUserRoles,
  getProps,
  /** @param {import('components/common/AccessControl').UseAccessControlProps} props  */
  (isAdmin, extensions, roles, props) => checkAccessControl({ isAdmin, extensions, roles, ...props })
);

export const reduceAccessControls = createSelector(
  getCurrentUserIsAdmin,
  getCurrentUserExtensions,
  getCurrentUserRoles,
  getProps,
  /** @param {Record<string, import('components/common/AccessControl').UseAccessControlProps>} records  */
  (isAdmin, extensions, roles, records) =>
    Object.entries(records).reduce((acc, [field, accessControl]) => {
      const isAllowed = checkAccessControl({ isAdmin, extensions, roles, ...accessControl });
      if (isAllowed) {
        return {
          ...acc,
          [field]: accessControl,
        };
      }
      return acc;
    }, /** @type {Record<string, import('components/common/AccessControl').UseAccessControlProps>} */ ({}))
);

const hasSchedulableRolesAccess = (allowedRoles, availableModes = [], selectedRoles = []) =>
  Object.entries(allowedRoles).reduce((acc, [mode, roles]) => {
    if (availableModes.includes(mode) && hasAccess(roles, selectedRoles)) {
      acc.push(mode);
    }
    return acc;
  }, []);

const currentUserHasSchedulableRoles = createSelector(
  isCurrentUserSalesManager,
  isCurrentUserPartsManager,
  isCurrentUserAfterSalesManager,
  isCurrentUserAdministrativeOfficer,
  isCurrentUserIsAdminOrGroupManager,
  (...roles) => roles.some(Boolean)
);

export const getSchedulableStatisticsExportMode = createSelector(
  getProps,
  currentUserHasSchedulableRoles,
  ({ roles: selectedRoles, availableModes }, canAccessToSchedulable) => {
    if (canAccessToSchedulable) {
      // If use is group manager, he has access to sales, after-sales and parts
      if (selectedRoles.includes(GROUP_MANAGER)) {
        return [SALES, TECHNICIAN, PARTS];
      }

      const allowedRoles = {
        [SALES]: [SALES_MANAGER],
        [TECHNICIAN]: [AFTER_SALES_MANAGER],
        [PARTS]: [PARTS_MANAGER],
      };

      return hasSchedulableRolesAccess(allowedRoles, availableModes, selectedRoles);
    }
    return [];
  }
);

export const getSchedulablePaymentExportMode = createSelector(
  getProps,
  currentUserHasSchedulableRoles,
  ({ roles: selectedRoles, availableModes, hasPayment }, canAccessToSchedulable) => {
    if (canAccessToSchedulable) {
      // If use is group manager, he has access to both sales and after sales
      const allowedRoles = {
        [SALES]: [SALES_MANAGER, BACK_OFFICE],
        [TECHNICIAN]: [AFTER_SALES_MANAGER, BACK_OFFICE],
        [PARTS]: [PARTS_MANAGER, BACK_OFFICE],
      };

      // If use is group manager, he has access to all modes that have payment
      if (selectedRoles.includes(GROUP_MANAGER)) {
        return Object.keys(allowedRoles);
      }

      // otherwise, we check if one of the dealers has payment and has the right role
      if (hasPayment) {
        return hasSchedulableRolesAccess(allowedRoles, availableModes, selectedRoles);
      }
    }
    return [];
  }
);

export const canSetSchedulableStatisticsExport = createSelector(
  getCurrentUserIsAdmin,
  isProfilePage,
  (isAdmin, isProfile) => isAdmin || isProfile
);

export const isImpersonatingUser = createSelector(getCurrentUser, state => state.impersonate);

export const displayImpersonatingBanner = createSelector(
  isImpersonatingUser,
  getCurrentUserIsLoading,
  (impersonating, loading) => impersonating && !loading
);

export const hasCurrentUserKioskPinLoginEnabled = createSelector(
  getCurrentUserAdditionalData,
  getCurrentUserIsAdmin,
  isImpersonatingUser,
  ({ enablePinLogin }, isAdmin, impersonatingUser) => !impersonatingUser && (enablePinLogin || isAdmin)
);

export const hasInterventionRole = createSelector(getCurrentUserRoles, roles =>
  hasAccess(roles, INTERVENTION_ROLES_WO_SELF_SERVICE));

export const currentUserHasSelfServiceOnly = createSelector(getCurrentUserData, ({ additionalData: user = {} }) => isSelfServiceOnly(user));

export const getFirstDealerIdOfCurrentUser = createSelector(getCurrentUserDealerIds, (currentUserDealerIds) => currentUserDealerIds?.[0]);

export const hasCurrentUserFeature = createSelector(
  getCurrentUserFeatures,
  getSecondProps,
  (features, feature) => features.includes(feature)
);
